/**
 * Utility functions for generating optimized image markup
 */

/**
 * Simple function to check if a file exists by loading it as an image
 * This avoids server requests for checking file existence
 */
function imageExists(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

/**
 * Generates a picture tag with appropriate srcsets for responsive images
 * This supports WebP and AVIF formats with proper fallbacks
 * 
 * @param {string} imagePath - The path to the image (without extension)
 * @param {Object} options - Configuration options
 * @returns {string} HTML picture tag
 */
export async function generatePictureTag(imagePath, options = {}) {
  const {
    alt = '',
    className = '',
    lazy = true,
    sizes = '(max-width: 480px) 100vw, (max-width: 768px) 50vw, 33vw',
    width = '',
    height = '',
    style = '',
  } = options;

  // Extract the base path without extension
  const basePath = imagePath.replace(/\.(jpg|jpeg|png|gif)$/, '');
  
  // Generate the responsive sizes strings
  const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];
  
  // Create the picture element container
  const picture = document.createElement('picture');
  
  // Check if AVIF is supported
  const avifExists = await imageExists(`${basePath}.avif`);
  if (avifExists) {
    // Create AVIF source element if format exists
    const avifSource = document.createElement('source');
    avifSource.type = 'image/avif';
    
    // Build srcset for AVIF
    const avifSrcsetParts = [];
    for (const size of breakpoints) {
      const sizeExists = await imageExists(`${basePath}-${size}.avif`);
      if (sizeExists) {
        const width = size === 'xs' ? 480 : size === 'sm' ? 768 : size === 'md' ? 992 : size === 'lg' ? 1200 : 1600;
        avifSrcsetParts.push(`${basePath}-${size}.avif ${width}w`);
      }
    }
    
    if (avifSrcsetParts.length > 0) {
      avifSource.srcset = avifSrcsetParts.join(', ');
      avifSource.sizes = sizes;
      picture.appendChild(avifSource);
    }
  }
  
  // Check if WebP is supported
  const webpExists = await imageExists(`${basePath}.webp`);
  if (webpExists) {
    // Create WebP source element if format exists
    const webpSource = document.createElement('source');
    webpSource.type = 'image/webp';
    
    // Build srcset for WebP
    const webpSrcsetParts = [];
    for (const size of breakpoints) {
      const sizeExists = await imageExists(`${basePath}-${size}.webp`);
      if (sizeExists) {
        const width = size === 'xs' ? 480 : size === 'sm' ? 768 : size === 'md' ? 992 : size === 'lg' ? 1200 : 1600;
        webpSrcsetParts.push(`${basePath}-${size}.webp ${width}w`);
      }
    }
    
    if (webpSrcsetParts.length > 0) {
      webpSource.srcset = webpSrcsetParts.join(', ');
      webpSource.sizes = sizes;
      picture.appendChild(webpSource);
    }
  }
  
  // Create JPEG source element (always included as fallback)
  const jpgSource = document.createElement('source');
  jpgSource.type = 'image/jpeg';
  
  // Build srcset for JPEG
  const jpgSrcsetParts = [];
  for (const size of breakpoints) {
    const sizeExists = await imageExists(`${basePath}-${size}.jpg`);
    if (sizeExists) {
      const width = size === 'xs' ? 480 : size === 'sm' ? 768 : size === 'md' ? 992 : size === 'lg' ? 1200 : 1600;
      jpgSrcsetParts.push(`${basePath}-${size}.jpg ${width}w`);
    }
  }
  
  if (jpgSrcsetParts.length > 0) {
    jpgSource.srcset = jpgSrcsetParts.join(', ');
    jpgSource.sizes = sizes;
    picture.appendChild(jpgSource);
  }
  
  // Create fallback img element
  const img = document.createElement('img');
  img.src = imagePath;
  img.alt = alt;
  img.className = className;
  
  if (lazy) {
    img.loading = 'lazy';
  }
  
  if (width) {
    img.width = width;
  }
  
  if (height) {
    img.height = height;
  }
  
  if (style) {
    img.style = style;
  }
  
  picture.appendChild(img);
  
  return picture;
}

/**
 * Creates a responsive background image CSS using the most optimal format
 * 
 * @param {HTMLElement} element - The element to apply background to
 * @param {string} imagePath - The path to the image (without extension)
 */
export async function setOptimizedBackground(element, imagePath) {
  if (!element) return;
  
  // Extract the base path without extension
  const basePath = imagePath.replace(/\.(jpg|jpeg|png|gif)$/, '');
  
  // Check format support in browser
  const supportsAvif = document.createElement('canvas')
    .toDataURL('image/avif').indexOf('data:image/avif') === 0;
    
  const supportsWebp = document.createElement('canvas')
    .toDataURL('image/webp').indexOf('data:image/webp') === 0;
  
  // Get viewport width
  const viewportWidth = window.innerWidth;
  let sizePrefix = '';
  
  if (viewportWidth <= 480) {
    sizePrefix = '-xs';
  } else if (viewportWidth <= 768) {
    sizePrefix = '-sm';
  } else if (viewportWidth <= 992) {
    sizePrefix = '-md';
  } else if (viewportWidth <= 1200) {
    sizePrefix = '-lg';
  } else {
    sizePrefix = '-xl';
  }
  
  // Check which formats exist
  const avifExists = supportsAvif && await imageExists(`${basePath}${sizePrefix}.avif`);
  const webpExists = supportsWebp && await imageExists(`${basePath}${sizePrefix}.webp`);
  const jpgExists = await imageExists(`${basePath}${sizePrefix}.jpg`);
  
  // Choose best available format
  let imageUrl;
  if (avifExists) {
    imageUrl = `${basePath}${sizePrefix}.avif`;
  } else if (webpExists) {
    imageUrl = `${basePath}${sizePrefix}.webp`;
  } else if (jpgExists) {
    imageUrl = `${basePath}${sizePrefix}.jpg`;
  } else {
    // Fallback to original image
    imageUrl = imagePath;
  }
  
  // Apply background
  element.style.backgroundImage = `url('${imageUrl}')`;
  
  // Update on resize for responsive sizes
  const handleResize = async () => {
    const newWidth = window.innerWidth;
    let newSizePrefix = '';
    
    if (newWidth <= 480) {
      newSizePrefix = '-xs';
    } else if (newWidth <= 768) {
      newSizePrefix = '-sm';
    } else if (newWidth <= 992) {
      newSizePrefix = '-md';
    } else if (newWidth <= 1200) {
      newSizePrefix = '-lg';
    } else {
      newSizePrefix = '-xl';
    }
    
    // Only update if size changed
    if (newSizePrefix !== sizePrefix) {
      // Check which formats exist at this size
      const newAvifExists = supportsAvif && await imageExists(`${basePath}${newSizePrefix}.avif`);
      const newWebpExists = supportsWebp && await imageExists(`${basePath}${newSizePrefix}.webp`);
      const newJpgExists = await imageExists(`${basePath}${newSizePrefix}.jpg`);
      
      // Choose best available format
      let newImageUrl;
      if (newAvifExists) {
        newImageUrl = `${basePath}${newSizePrefix}.avif`;
      } else if (newWebpExists) {
        newImageUrl = `${basePath}${newSizePrefix}.webp`;
      } else if (newJpgExists) {
        newImageUrl = `${basePath}${newSizePrefix}.jpg`;
      } else {
        // Fallback to original image
        newImageUrl = imagePath;
      }
      
      // Update background
      element.style.backgroundImage = `url('${newImageUrl}')`;
    }
  };
  
  // Throttled resize handler
  let resizeTimeout;
  const resizeThrottler = () => {
    if (!resizeTimeout) {
      resizeTimeout = setTimeout(() => {
        resizeTimeout = null;
        handleResize();
      }, 200);
    }
  };
  
  window.addEventListener('resize', resizeThrottler);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('resize', resizeThrottler);
  };
}

/**
 * Replaces all image tags with data-src attributes with optimized picture elements
 */
export async function enhanceImages() {
  try {
    // Find all images with data-src attributes
    const images = document.querySelectorAll('img[data-src]');
    
    // Process each image
    for (const img of images) {
      const imagePath = img.getAttribute('data-src');
      const alt = img.getAttribute('alt') || '';
      const className = img.className || '';
      const width = img.getAttribute('width') || '';
      const height = img.getAttribute('height') || '';
      const style = img.getAttribute('style') || '';
      
      try {
        // Generate picture element
        const pictureElement = await generatePictureTag(imagePath, {
          alt,
          className,
          lazy: true,
          width,
          height,
          style
        });
        
        // Replace the img with the picture element
        if (pictureElement && img.parentNode) {
          img.parentNode.replaceChild(pictureElement, img);
        }
      } catch (err) {
        console.warn(`Failed to enhance image ${imagePath}:`, err);
        // Keep the original image if enhancement fails
      }
    }
    
    // Find all elements with data-bg attributes for background images
    const bgElements = document.querySelectorAll('[data-bg]');
    
    // Process each background element
    for (const element of bgElements) {
      const imagePath = element.getAttribute('data-bg');
      
      try {
        await setOptimizedBackground(element, imagePath);
        
        // Remove the data-bg attribute to prevent re-processing
        element.removeAttribute('data-bg');
      } catch (err) {
        console.warn(`Failed to set optimized background for ${imagePath}:`, err);
        // Set a simple background as fallback
        element.style.backgroundImage = `url('${imagePath}')`;
      }
    }
    
    console.log('✅ Enhanced images with responsive formats');
  } catch (err) {
    console.error('❌ Error enhancing images:', err);
  }
}

// Export default for direct import
export default { generatePictureTag, setOptimizedBackground, enhanceImages }; 